import React, { useContext } from "react";

import { useThemeSwitcher } from "@local-logic/core/ui";

import LocalLogicClient from "@local-logic/client";
import { ProductNames } from "~/types";
import type {
  Environment,
  Appearance,
  Locale,
  ExternalId,
} from "~/types/options";

interface LocalLogicProviderProps {
  apiKey?: string;
  externalId?: string;
  appearance?: Appearance;
  children: React.ReactNode;
  locale?: Locale;
  product?: ProductNames;
  environment?: Environment["api"];
}

interface DefaultContext {
  client: ReturnType<typeof LocalLogicClient>;
  options: {
    externalId?: ExternalId;
    appearance?: Appearance;
  };
}

const LocalLogicContext = React.createContext<DefaultContext>(
  {} as DefaultContext
);

const useLocalLogic = () => useContext(LocalLogicContext);

const LocalLogicProvider = ({
  appearance,
  apiKey,
  externalId,
  children,
  locale,
  product,
  environment,
}: LocalLogicProviderProps) => {
  useThemeSwitcher({
    themeName: appearance?.theme,
    overrides: appearance?.variables,
  });

  if (!apiKey) {
    throw Error("A valid apiKey must be provided.");
  }

  const client = LocalLogicClient(apiKey, {
    locale,
    product,
    environment,
  });

  return (
    <LocalLogicContext.Provider
      value={{
        client,
        options: {
          externalId,
          appearance,
        },
      }}
    >
      {children}
    </LocalLogicContext.Provider>
  );
};

export { useLocalLogic, LocalLogicProvider, LocalLogicContext };
